import { Func } from 'helpers/typescript';
import { ON_CLIENT } from '..';

/* eslint-disable no-console */
export const RED = '\u001b[1;31m';
export const GREEN = '\u001b[1;32m';
export const YELLOW = '\u001b[1;33m';
export const BLUE = '\u001b[1;34m';
export const RESET_COLOR = '\u001B[0m';

const LOG_LEVEL_DEBUG = process.env.NODE_ENV === 'development';
const LOG_LENGTH = 200;

type LogLevel = 'log' | 'warn' | 'info' | 'error';

type LogItem = {
  level: LogLevel;
  timestamp: string;
  location: string;
  executionTime?: number;
  log: any;
  context: string;
};

type AddToLogItem = {
  level: LogLevel;
  location: string;
  executionTime?: number;
  log: any;
  context: string;
};

const liveLog: LogItem[] = [];

const addToLog = (item: AddToLogItem): void => {
  // Maintains upto 200 items in memory... just incase...
  if (liveLog.length >= LOG_LENGTH) {
    liveLog.slice(-LOG_LENGTH);
  }

  liveLog.push({ ...item, timestamp: new Date().toISOString() });
};

// [LOG] 2022-01-01T01:01:01.000Z /bet/1c2f3d69-ae0e-4e44-8560-53c99d33349b getDefaultProps [Oject object]

const getColouredTime = (time: number): string => {
  if (time > 1000) return `🐌 ${RED}${time}ms${RESET_COLOR}`;
  if (time > 50) return `🏎  ${YELLOW}${time}ms${RESET_COLOR}`;
  return `🚀  ${GREEN}${time}ms${RESET_COLOR}`;
};

const timeSync = <TProps extends any[], TReturn>(
  callback: Func<TProps, TReturn>,
  name: string,
) => (...props: TProps): TReturn => {
  const startTime = Date.now();

  const result = callback(...props);
  const timeTaken = Date.now() - startTime;

  if (LOG_LEVEL_DEBUG) {
    const lineToLog = `${getColouredTime(timeTaken)} - sync - ${name}`;
    console.info(lineToLog);
    addToLog({
      level: 'info',
      log: lineToLog,
      location: ON_CLIENT ? window.location.pathname : 'SSR',
      executionTime: timeTaken,
      context: `timeSync - ${name}`,
    });
  }

  return result;
};

const timeAsync = <TProps extends any[], TReturn>(
  callback: Func<TProps, Promise<TReturn>>,
  name: string,
) => async (...props: TProps): Promise<TReturn> => {
  const startTime = Date.now();

  const result = await callback(...props);
  const timeTaken = Date.now() - startTime;

  if (LOG_LEVEL_DEBUG) {
    const lineToLog = `${getColouredTime(timeTaken)} - async - ${name}`;
    console.info(lineToLog);
    addToLog({
      level: 'info',
      log: lineToLog,
      location: ON_CLIENT ? window.location.pathname : 'SSR',
      executionTime: timeTaken,
      context: `timeAsync - ${name}`,
    });
  }

  return result;
};

const logger = (
  level: LogLevel,
  log: any,
  options: {
    location: string;
    context: string;
    executionTime?: number;
  },
): void => {
  if (LOG_LEVEL_DEBUG) {
    console.info(log);
  }
  addToLog({
    level,
    log,
    ...options,
  });
};

export { liveLog, timeSync, timeAsync, logger };
